import {
  join,
  map,
  reduce,
  extend,
  split,
  compact,
  isString,
  isEmpty,
  isNumber,
  isFinite,
  toNumber,
  size,
  trimStart,
  replace,
  endsWith,
  startsWith,
  slice,
} from 'lodash';

import {
  ASSET_HOST,
  BASE_PATH,
  BASE_REMOTE_PATH,
  IMAGE_HOST,
  VIDEO_HOST,
  V2_DEFAULT_TRANSFORMATIONS,
} from 'constants/cloudinary';
import RemoteAssetSource from 'utils/RemoteAssetSource';

import Transformation from './Transformation';

const Cloudinary = class {
  static generateImageUrl = ({ publicId, transformations = '' }) => {
    if (!publicId) {
      throw new Error('utils:Cloudinary.generateImageUrl requires the "publicId" argument!');
    }

    if (transformations && !isString(transformations)) {
      throw new Error(
        'utils:Cloudinary.generateImageUrl requires a valid Cloudinary "transformations" argument! Check the docs: https://cloudinary.com/documentation/image_transformation_reference'
      );
    }

    return join(compact([ASSET_HOST, BASE_PATH, transformations, publicId]), '/');
  };

  static generateRemoteImageUrl = ({ remoteUrl, transformations }) => {
    if (!remoteUrl) {
      throw new Error('utils:Cloudinary.generateRemoteImageUrl requires the "remoteUrl" argument!');
    }

    if (transformations && !isString(transformations)) {
      throw new Error(
        'utils:Cloudinary.generateRemoteImageUrl requires a valid Cloudinary "transformations" argument! Check the docs: https://cloudinary.com/documentation/image_transformation_reference'
      );
    }
    const completeRemoteUrl = remoteUrl.startsWith('//') ? `https:${remoteUrl}` : remoteUrl;

    return join(compact([ASSET_HOST, BASE_REMOTE_PATH, transformations, completeRemoteUrl]), '/');
  };

  /* legacy above -- to be removed */

  static get defaultTransformations() {
    return V2_DEFAULT_TRANSFORMATIONS;
  }

  static get assetHosts() {
    return {
      video: VIDEO_HOST,
      image: IMAGE_HOST,
    };
  }

  static get transformations() {
    return {
      width: new Transformation({
        type: 'width',
        validator(value) {
          const isValid = isNumber(value);

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use integers.`
          );
        },
      }),

      height: new Transformation({
        type: 'height',
        validator(value) {
          const isValid = isNumber(value);

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use integers.`
          );
        },
      }),

      format: new Transformation({
        type: 'format',
        validator(value) {
          const isValid = value === 'auto';

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use "auto".`
          );
        },
      }),

      quality: new Transformation({
        type: 'quality',
        validator(value) {
          const isValid = value === 'auto';

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use "auto".`
          );
        },
      }),

      devicePixelRatio: new Transformation({
        type: 'devicePixelRatio',
        validator(value) {
          const isValid = isNumber(value) && value <= 3;

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use an integer less than or equal to 3.`
          );
        },
      }),

      backgroundColor: new Transformation({
        type: 'backgroundColor',
        validator(value) {
          const isValid =
            isString(value) &&
            size(value) <= 7 &&
            /^#([A-Fa-f0-9]{3}$)|([A-Fa-f0-9]{6}$)/.test(value);

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use valid CSS hex values.`
          );
        },
        formatter(value) {
          return `${this.prefix}${trimStart(value, '#')}`;
        },
      }),

      transformation: new Transformation({
        type: 'transformation',
        validator(value) {
          const validOptions = {
            relative: true,
            attachment: true,
            layer_apply: true,
          };

          const isValid = !!validOptions[value];

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${
              this.type
            }:${value} is malformed. Please use one of ${JSON.stringify(
              Object.keys(validOptions)
            )}.`
          );
        },
      }),

      overlay: new Transformation({
        type: 'overlay',
        validator(value) {
          const validOptions = {
            'play-button-png': true,
          };

          const isValid = !!validOptions[value];

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${
              this.type
            }:${value} is malformed. Please use one of the following values: ${JSON.stringify(
              Object.keys(validOptions)
            )}.`
          );
        },
      }),

      startOffset: new Transformation({
        type: 'startOffset',
        validator(value) {
          const isSeconds = isFinite(toNumber(value));
          const isPercentage = endsWith(value, 'p') && isFinite(toNumber(value.slice(0, -1)));

          const isValid = isSeconds || isPercentage;

          return Transformation.returnErrorIfInvalid(
            isValid,
            `utils:Cloudinary.validateTransformation - transformation:${this.type}:${value} is malformed. Please use a number (e.g. "5" for 5 seconds) or a percentage (e.g. "25p" for 25 percent)`
          );
        },
      }),
    };
  }

  static buildAutoUploadUrl(url, transformations = {}) {
    const assetType = RemoteAssetSource.parseRequestType(url);
    const assetHost = Cloudinary.assetHosts[assetType];
    const uploadFolder = RemoteAssetSource.parseCloudinaryAutoUploadFolder(url);
    const requestPath = RemoteAssetSource.parseRequestPath(url);

    return `${assetHost}/${Cloudinary.buildTransformationString(
      transformations
    )}/${uploadFolder}/${requestPath}`;
  }

  static buildTransformationSubString(type, value) {
    const transformation = Cloudinary.transformations[type];

    return transformation.format(value);
  }

  static buildTransformationString(requestedTransformations = {}) {
    const transformations = extend({}, Cloudinary.defaultTransformations, requestedTransformations);
    const errors = Cloudinary.validateTransformations(transformations);

    if (errors) {
      throw new Error(join(errors, '\n'));
    }

    return join(
      map(transformations, (value, type) => Cloudinary.buildTransformationSubString(type, value)),
      ','
    );
  }

  static validateTransformation(type, value) {
    const transformation = Cloudinary.transformations[type];

    if (!transformation) {
      return `utils:Cloudinary.validateTransformation - transformation:${type} is not supported.`;
    }

    return transformation.validate(value);
  }

  static validateTransformations(transformations) {
    const errors = reduce(
      transformations,
      (accumulator, value, type) => {
        const error = Cloudinary.validateTransformation(type, value);

        return error ? [error, ...accumulator] : accumulator;
      },
      []
    );

    return isEmpty(errors) ? undefined : errors;
  }

  static modifyFileExtension = (src, newExtension) => {
    if (!src) {
      return null;
    }

    if (!newExtension) {
      return src;
    }

    const sanitizedExtension = startsWith(newExtension, '.')
      ? replace(newExtension, '.', '')
      : newExtension;

    const extensionSeparator = '.';
    const srcSegments = split(src, extensionSeparator);

    if (srcSegments.length < 2) {
      return src;
    }

    const srcWithoutExtension = slice(srcSegments, 0, -1);

    return [...srcWithoutExtension, sanitizedExtension].join(extensionSeparator);
  };

  static generateVideoThumbnail = (src, options = { showOverlay: true }) => {
    if (!src) {
      return null;
    }

    const imageSrc = Cloudinary.modifyFileExtension(src, '.jpg');

    /**
     * Transformations:
     *  * so_25p => take screenshot at 25% of video
     *  * l_play-button-png => overlay with image of public id "play-button-png"
     *  * fl_relative, w_0.5 => make play button half the width of whatever it's overlayed on top of
     */

    const transformations = {
      quality: 'auto',
      format: 'auto',
      startOffset: '25p',
      transformation: 'relative',
      width: 0.5,
    };

    if (options.showOverlay) {
      transformations.overlay = 'play-button-png';
    }

    const overlayedImage = Cloudinary.buildAutoUploadUrl(imageSrc, transformations);

    return overlayedImage;
  };
};

export default Cloudinary;
